import axios from "axios";
import { haversineMeters, newSessionToken, readLatLng, toLatLng } from "../utils/googleMapsUtils";

// Para no enviar los headers de autenticacion de mlpoint.
const googleAxios = axios.create({ withCredentials: false });

const PLACES_AUTOCOMPLETE_URL =
  "https://places.googleapis.com/v1/places:autocomplete";
const ROUTES_URL = "https://routes.googleapis.com/directions/v2:computeRoutes";
const ADDRESS_TYPES = ["premise", "subpremise", "street_address", "route"];
const DRIVING_ROUTE_FIELDS = [
  "routes.viewport",
  "routes.polyline.encodedPolyline",
];

export const GOOGLE_MAPS_GET_PLACE_SUGGESTIONS = "GOOGLE_MAPS_GET_PLACE_SUGGESTIONS";
export const GOOGLE_MAPS_GET_PLACE_DETAILS = "GOOGLE_MAPS_GET_PLACE_DETAILS";
export const GOOGLE_MAPS_COMPUTE_DRIVING_ROUTE = "GOOGLE_MAPS_COMPUTE_DRIVING_ROUTE";
export const GOOGLE_MAPS_COMPUTE_ROUTES_DISTANCE = "GOOGLE_MAPS_COMPUTE_ROUTES_DISTANCE";

const durationToMillis = (duration) => {
  if (typeof duration !== "string") return null;
  const seconds = Number.parseFloat(duration);
  return Number.isFinite(seconds) ? Math.round(seconds * 1000) : null;
};

const routeBody = (from, to) => ({
  origin: {
    location: { latLng: { latitude: from.lat, longitude: from.lng } },
  },
  destination: {
    location: { latLng: { latitude: to.lat, longitude: to.lng } },
  },
  travelMode: "DRIVE",
  routingPreference: "TRAFFIC_UNAWARE",
});

export const getPlaceSuggestions = (
  apiKey,
  input,
  sessionToken,
  { restrictToAddress = false } = {},
) => (dispatch) =>
    dispatch({
      type: GOOGLE_MAPS_GET_PLACE_SUGGESTIONS,
      payload: googleAxios
        .post(
          PLACES_AUTOCOMPLETE_URL,
          {
            input,
            includedRegionCodes: ["ar"],
            sessionToken,
            ...(restrictToAddress ? { includedPrimaryTypes: ADDRESS_TYPES } : {}),
          },
          { headers: { "X-Goog-Api-Key": apiKey } },
        )
        .then((response) => ({
          data: (response.data.suggestions || [])
            .map((suggestion) => {
              const prediction = suggestion.placePrediction;
              if (!prediction?.placeId) return null;
              return {
                placeId: prediction.placeId,
                text: prediction.text,
                mainText: prediction.structuredFormat?.mainText,
                secondaryText: prediction.structuredFormat?.secondaryText,
              };
            })
            .filter(Boolean),
        })),
    });

export const getPlaceDetails = (apiKey, placeId, sessionToken, fieldMask) => (dispatch) =>
  dispatch({
    type: GOOGLE_MAPS_GET_PLACE_DETAILS,
    payload: googleAxios
      .get(`https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}`, {
        params: sessionToken ? { sessionToken } : undefined,
        headers: {
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask": fieldMask,
        },
      })
      .then((response) => ({ data: response.data })),
  });

export const geocodeAddress = (apiKey, input) => async (dispatch) => {
  const sessionToken = newSessionToken();
  const suggestions = await dispatch(getPlaceSuggestions(apiKey, input, sessionToken));
  const prediction = suggestions?.value?.data?.[0];
  if (!prediction) return { value: { data: null } };

  const place = await dispatch(
    getPlaceDetails(apiKey, prediction.placeId, sessionToken, "location"),
  );
  return { value: { data: toLatLng(place?.value?.data?.location) } };
};

export const computeDrivingRoute = (apiKey, origin, destination) => (dispatch) => {
  const from = readLatLng(origin);
  const to = readLatLng(destination);
  if (!apiKey || !from || !to) return Promise.resolve({ value: { data: null } });

  return dispatch({
    type: GOOGLE_MAPS_COMPUTE_DRIVING_ROUTE,
    payload: googleAxios
      .post(ROUTES_URL, routeBody(from, to), {
        params: { fields: DRIVING_ROUTE_FIELDS.join(",") },
        headers: { "X-Goog-Api-Key": apiKey },
      })
      .then((response) => ({ data: response.data.routes?.[0] ?? null })),
  });
};

const straightLineDistance = (from, to) => ({
  data: {
    distanceMeters: haversineMeters(from, to),
    durationMillis: null,
    route: null,
  },
});

export const computeRoutesDistance = (apiKey, origin, destination) => (dispatch) => {
  const from = readLatLng(origin);
  const to = readLatLng(destination);
  if (!from || !to) return Promise.resolve({ value: { data: null } });
  if (!apiKey) return Promise.resolve({ value: straightLineDistance(from, to) });

  return dispatch({
    type: GOOGLE_MAPS_COMPUTE_ROUTES_DISTANCE,
    payload: googleAxios
      .post(ROUTES_URL, routeBody(from, to), {
        headers: {
          "X-Goog-Api-Key": apiKey,
          "X-Goog-FieldMask": "routes.distanceMeters,routes.duration",
        },
      })
      .then((response) => {
        const route = response.data.routes?.[0];
        const distanceMeters = Number(route?.distanceMeters);
        if (!Number.isFinite(distanceMeters)) return straightLineDistance(from, to);
        return {
          data: {
            distanceMeters,
            durationMillis: durationToMillis(route.duration),
            route,
          },
        };
      })
      .catch(() => straightLineDistance(from, to)),
  });
};
